import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* ============================================================
   Sidebar "Aurora" — escura e sofisticada nos dois modos.
   Cores vêm de CSS variables definidas em ThemeModeContext.
   ============================================================ */

/* Glow ambiente: respira bem devagar para dar vida sem disputar atenção
   com o conteúdo. Só roda para quem não pediu menos movimento. */
const auroraBreatheTop = keyframes`
  0%, 100% { opacity: 0.75; transform: scale(1); }
  50%      { opacity: 1; transform: scale(1.12); }
`;

const auroraBreatheBottom = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.08); }
`;

export const SidebarContainer = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  width: ${({ $collapsed }) => ($collapsed ? '76px' : '264px')};
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100vh;
  padding: ${({ $collapsed }) => ($collapsed ? '18px 12px' : '20px 16px')};
  overflow-x: hidden;
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1), padding 0.28s ease;
  flex-shrink: 0;
  border-right: 1px solid var(--sidebar-border);

  /* brilho ambiente no topo */
  &::before {
    content: '';
    position: absolute;
    top: -120px;
    left: -40px;
    width: 240px;
    height: 240px;
    background: radial-gradient(circle, rgba(43, 123, 255, 0.35), transparent 65%);
    filter: blur(20px);
    pointer-events: none;
    transform-origin: center;
    animation: ${auroraBreatheTop} 7s ease-in-out infinite;
  }

  /* segundo brilho, mais discreto, no rodapé — dá profundidade sem repetir */
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -60px;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(34, 211, 238, 0.22), transparent 68%);
    filter: blur(24px);
    pointer-events: none;
    transform-origin: center;
    animation: ${auroraBreatheBottom} 9s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none;
    }
  }
`;

export const LogoSection = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--sidebar-border);

  img {
    width: ${({ $collapsed }) => ($collapsed ? '40px' : '170px')};
    transition: width 0.28s ease;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }

  p {
    font-size: 11px;
    letter-spacing: 0.3px;
    color: var(--sidebar-text-muted);
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  }
`;

export const Menu = styled.div`
  flex: 1;
  margin-top: 18px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 3px;
  }
`;

export const MenuSection = styled.div<{ $collapsed?: boolean }>`
  margin: 12px 0 4px;

  &:before {
    content: attr(title);
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
    font-size: 10px;
    font-weight: 700;
    color: var(--sidebar-text-muted);
    margin: 6px 0;
    padding-left: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

/* Borda do item selecionado deriva devagar pelas três cores da marca —
   o mesmo gradiente "Aurora" do resto do sistema, só que respirando. */
const auroraSelectedGlow = keyframes`
  0%, 100% {
    border-color: rgba(43, 123, 255, 0.4);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(43, 123, 255, 0.14);
  }
  33% {
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(59, 130, 246, 0.14);
  }
  66% {
    border-color: rgba(34, 211, 238, 0.4);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(34, 211, 238, 0.14);
  }
`;

export const MenuItem = styled(Link)<{ $collapsed?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 11px 14px;
  text-decoration: none;
  color: var(--sidebar-text);
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
  white-space: nowrap;
  overflow: hidden;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  border: 1px solid transparent;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease,
    border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: var(--sidebar-hover);
    color: #fff;
    transform: translateX(2px);
  }

  svg {
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '13px')};
    font-size: 19px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.08);
  }

  /* item ativo: só a pílula cinza fosca já basta pra sinalizar "você está
     aqui" — sem traço lateral colorido disputando atenção. A borda, porém,
     respira bem devagar pelas cores da marca: um brilho vivo, não um bloco
     de cor parado. */
  &.active {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.045)
    );
    border-color: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-weight: 600;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(43, 123, 255, 0.12);
    animation: ${auroraSelectedGlow} 8s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

export const SubMenuToggle = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  padding: 11px 14px;
  color: var(--sidebar-text);
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--sidebar-hover);
    color: #fff;
  }

  svg {
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '13px')};
    font-size: 19px;
    flex-shrink: 0;
  }
`;

/* Halo que respira, para o olho achar a novidade sem o selo gritar.
   Um ciclo longo de propósito: é aviso, não alarme. */
const newPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.55); }
  70%  { box-shadow: 0 0 0 6px rgba(251, 191, 36, 0); }
  100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
`;

/** Selo "Novo" dos itens recém-lançados. Some sozinho — ver `newMenuItems.ts`. */
export const NewBadge = styled.span`
  /* empurra para a ponta direita do item, longe do rótulo */
  margin-left: auto;
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 999px;
  /* Âmbar, não azul: o item selecionado vira uma pílula com o gradiente da
     marca, e um selo azul sumiria dentro dela. Âmbar é o complementar do
     azul, então destaca tanto no menu parado quanto no item selecionado. */
  background: var(--gradient-highlight);
  /* texto escuro porque o âmbar é claro demais para carregar branco */
  color: #241802;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.5;
  text-transform: uppercase;
  animation: ${newPulse} 2.8s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Selo de alerta (produtos/tarefas/pendências/tickets em aberto).
   `margin-left: auto` é o que empurra o sino pra ponta do item, longe do
   rótulo — sem isso ele fica colado no texto. */
export const AlertBell = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 3px 9px 3px 7px;
  border: 1px solid rgba(251, 113, 133, 0.4);
  border-radius: 999px;
  background: rgba(251, 113, 133, 0.14);
  color: var(--accent-rose);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgba(251, 113, 133, 0.26);
    border-color: rgba(251, 113, 133, 0.7);
    box-shadow: 0 0 0 4px rgba(251, 113, 133, 0.12);
  }

  &:active {
    transform: scale(0.94);
  }

  svg {
    font-size: 14px;
    display: block;
    flex-shrink: 0;
  }
`;

/* Versão recolhida: um ponto no canto do item, sem disputar espaço com o
   ícone centralizado. MenuItem já é position:relative. */
export const AlertDot = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  width: 9px;
  height: 9px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--accent-rose);
  box-shadow: 0 0 0 2px var(--sidebar-bg), 0 0 9px rgba(251, 113, 133, 0.85);
  cursor: pointer;
`;

export const UserCard = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  align-items: center;
  padding: ${({ $collapsed }) => ($collapsed ? '8px' : '12px')};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--sidebar-border);
  border-radius: 16px;
  backdrop-filter: blur(6px);
  position: relative;

  .avatar {
    width: ${({ $collapsed }) => ($collapsed ? '40px' : '46px')};
    height: ${({ $collapsed }) => ($collapsed ? '40px' : '46px')};
    border-radius: 50%;
    overflow: hidden;
    background: var(--gradient-brand);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 6px 16px rgba(43, 123, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1rem;
    color: #fff;
    margin-right: ${({ $collapsed }) => ($collapsed ? '0' : '11px')};
    flex-shrink: 0;
  }

  .avatar-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  .info {
    text-align: left;
    overflow: hidden;
    flex: 1;
    min-width: 0;
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
    strong {
      display: block;
      color: #fff;
      font-size: 0.86rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    small {
      font-size: 0.66rem;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: var(--accent-3);
    }
  }
`;

export const LogoutButton = styled.button<{ $collapsed?: boolean }>`
  background: rgba(251, 113, 133, 0.12);
  border: none;
  color: #fb7185;
  font-size: 14px;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  cursor: pointer;
  border-radius: 10px;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;

  &:hover {
    background: rgba(251, 113, 133, 0.22);
    color: #ff8fa3;
    transform: scale(1.05);
  }

  svg {
    font-size: 18px;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 14px 2px 2px;
`;
